import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Route, Routes, BrowserRouter as Router, Navigate, useLocation } from 'react-router';
import { closeError } from '../actions/index';
import { autoQueryStringLogin, checkForToken } from '../actions/actions_auth';
// eslint-disable-next-line import/no-cycle
import DynamicLayoutContainer from '@/containers/layout/DynamicLayoutContainer';

import Login from '../containers/pages/Login';
import './App.scss';
import './analyticDashboard.scss';
import MaintenanceMode from './pages/MaintenanceMode';
import ResetPassword from './pages/ResetPassword';
import browserHistory from './utilities/BrowserHistory';
import AppRoutes from './AppRoutes';
import AppError from '../containers/utilities/AppError';
import './utilities/FetchInterceptor';
import { Flex, Spinner } from '@chakra-ui/react';
import useUserpilot from '@/hooks/useUserpilot';

function getParameterByName(name, url = window?.location?.href) {
  const nameWithRegex = name.replace(/[[\]\\]/g, '\\$&');
  const regex = new RegExp(`[?&]${nameWithRegex}(=([^&#]*)|&|#|$)`);

  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

// Wrapper component to use useLocation inside Router context
function UserpilotWrapper({ user }) {
  const location = useLocation();
  
  // Prepare user data for Userpilot
  const userpilotUser = user?.id && user?.organisation?.id
    ? {
        id: String(user.id),
        firstName: user.first_name,
        lastName: user.last_name,
        name: [user.first_name, user.last_name].filter(Boolean).join(' '),
        email: user.email,
        companyId: String(user.organisation.id),
        companyName: user.organisation.name,
      }
    : null;

  useUserpilot(userpilotUser, location.pathname);
  
  return null;
}

class App extends Component {
  constructor(props) {
    super(props);
    let url = window.location?.pathname + window.location.search;
    if (url.indexOf('?') > -1) {
      url = url.substring(0, url.indexOf('?'));
    }
    this.state = {
      relative_path: window.location?.pathname + window.location.search,
      // path_no_query_string: url,
      loading: true,
      isMaintenanceMode: false,
      hasMaintenanceAccess: false,
    };
  }

  checkMaintenanceMode() {
    const { user } = this.props;

    // Check if maintenance mode is enabled
    const maintenanceMode = process.env.REACT_APP_BOAB_MAINTENANCE === 'yes';

    // Get allowed emails from environment variable
    const allowedEmailsString = process.env.REACT_APP_BOAB_MAINTENANCE_SITE_ALLOWED_EMAILS || '';
    const allowedEmails = allowedEmailsString
      .split(',')
      .map(email => email.trim().toLowerCase())
      .filter(email => email.length > 0);

    // Check if current user has access during maintenance
    let hasAccess = false;
    if (user && user.email) {
      const userEmail = user.email.toLowerCase();
      hasAccess = allowedEmails.includes(userEmail);
    }

    this.setState({
      isMaintenanceMode: maintenanceMode,
      hasMaintenanceAccess: hasAccess,
    });

    return maintenanceMode;
  }

  componentDidMount() {
    const { relative_path } = this.state;
    const { dispatch } = this.props;
    const isInMaintenanceMode = this.checkMaintenanceMode();

    if (
      !isInMaintenanceMode ||
      relative_path.indexOf('/login') !== -1 ||
      relative_path.indexOf('/reset-password') !== -1
    ) {
      // Check for automatic login via querystring
      // Exception for reset password page
      if (relative_path.indexOf('/reset-password') === -1 && relative_path.indexOf('/login') === -1) {
        if (getParameterByName('token')) {
          dispatch(autoQueryStringLogin(getParameterByName('token')));
          window.history.pushState(null, '', window.location.href.split('?')[0]);
        }

        if (!isInMaintenanceMode) {
          dispatch(checkForToken(browserHistory));
        }
      }
    }

    setTimeout(() => this.setState({ loading: false }), 200);
  }

  componentDidUpdate(prevProps) {
    const { user, dispatch } = this.props;

    if (user !== prevProps.user) {
      const isInMaintenanceMode = this.checkMaintenanceMode();

      // Only redirect to login if NOT in maintenance mode
      if (!user && !isInMaintenanceMode) {
        browserHistory.push('/login');
      }
    }

    // Remove old error states that are not relevant for updated components
    dispatch(closeError(false));
  }

  render() {
    const { user } = this.props;
    const { loading, isMaintenanceMode, hasMaintenanceAccess } = this.state;
    if (loading) {
      return (
        <Flex h="100vh" align="center" justify="center">
          <Spinner />
        </Flex>
      );
    }

    if (isMaintenanceMode) {
      if (user && !hasMaintenanceAccess) {
        return <MaintenanceMode />;
      }

      if (!user) {
        const isLoginRoute =
          window.location.pathname === '/login' || window.location.pathname.includes('reset-password');

        if (isLoginRoute) {
          return (
            <Router>
              <div>
                <AppError />
                <Routes>
                  {window.location.pathname.includes('reset-password') ? (
                    <Route path="*" element={<ResetPassword />} />
                  ) : (
                    <Route path="*" element={<Login />} />
                  )}
                </Routes>
              </div>
            </Router>
          );
        }
        return <MaintenanceMode />;
      }
    }

    return (
      <Router>
        <UserpilotWrapper user={user} />
        <div>
          <AppError />
          {user ? (
            <DynamicLayoutContainer>
              <AppRoutes nextGen={user.next_gen_ui} />
            </DynamicLayoutContainer>
          ) : (
            <Routes>
              {window.location.pathname.includes('reset-password') ? (
                <Route path="*" element={<ResetPassword />} />
              ) : (
                <Route path="/login" element={<Login />} />
              )}
            </Routes>
          )}
        </div>
        <Routes>
          <Route path="/logout" element={<Navigate key={Math.random()} to="/login" replace />} />
        </Routes>
      </Router>
    );
  }
}

App.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    next_gen_ui: PropTypes.bool,
  }),
  dispatch: PropTypes.func.isRequired,
};

export default App;
